import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

// Module-level cache so we don't refetch the group list per row.
let _cachedGroups = null;
let _fetching = null;

async function fetchGroups() {
  if (_cachedGroups) return _cachedGroups;
  if (_fetching) return _fetching;
  _fetching = api
    .get("/service-groups")
    .then(({ data }) => {
      _cachedGroups = data?.groups || [];
      _fetching = null;
      return _cachedGroups;
    })
    .catch(() => {
      _fetching = null;
      return [];
    });
  return _fetching;
}

/**
 * Searchable service-group picker (Command/Combobox pattern).
 * Value is the group's exact string label; empty string = ungrouped.
 */
export const GroupCombobox = ({ value, onChange, testId = "group-combobox" }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchGroups().then((g) => {
      if (alive) setGroups(g);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between rounded-md border-slate-300 text-xs h-8 px-2 max-w-[180px]"
          data-testid={testId}
        >
          <span className="truncate">
            {value || <span className="text-slate-400">Group…</span>}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search group…" className="h-9" />
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty>No group found.</CommandEmpty>
            <CommandGroup>
              {value ? (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="text-slate-500"
                  data-testid={`${testId}-clear`}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear group
                </CommandItem>
              ) : null}
              {groups.map((g) => (
                <CommandItem
                  key={g}
                  value={g}
                  onSelect={(v) => {
                    // v is lowercased by cmdk — pick from the canonical list
                    const picked = groups.find((x) => x.toLowerCase() === v) || v;
                    onChange(picked);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${g}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === g ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {g}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
